import NoData from '@/components/common/NoData'
import useQuickAddNames from '@/features/config/hooks/useQuickAddNames'
import { WHOLE_LIST_PARAMS } from '@/constant/common'
import useSearchEmitter from '@/features/config/hooks/emitter/useSearchEmitter'
import QuickAddChips from '@/features/config/components/common/QuickAddChips'
import {
  EMITTER_EFFICIENCY_PRESETS,
  EMITTER_PRESETS,
} from '@/constant/quickAddPresets'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import { useGuidedEdit } from '@/features/config/contexts/guided-edit-context'
import SearchInput from '@/components/common/SearchInput'
import ButtonField from '@/components/form-control/ButtonField'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { flyInVariantsDashboard } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import { SelectChangeEvent, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useConfigUI } from '../../context'
import EmitterTable, { type EmitterGridActions } from './EmitterTable'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

interface EmitterProps {
  // When set, replaces the search box in the toolbar (used by the combined
  // Plant / Soil / Emitter view to put the column title on the toolbar row).
  titleSlot?: ReactNode
  // Scopes the grid to an explicit controller (guided setup, SCRUM-1032). When
  // omitted, the config screens fall back to the globally-selected interface.
  interfaceId?: number | string
}

const Emitter = ({ titleSlot, interfaceId: propInterfaceId }: EmitterProps) => {
  const MotionDiv = motion.div
  const { openModal, setModalView } = useConfigUI()
  const { interfaceId: authInterfaceId } = useAuthContext()
  const interfaceId = propInterfaceId ?? authInterfaceId
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  // SCRUM-1032: inside the guided setup the grid opens in bulk edit and its
  // pending rows are flushed when the wizard moves on.
  const bulkEditByDefault = useGuidedEdit('emitter', () =>
    gridActions.current?.save(),
  )
  const [bulkEditMode, setBulkEditMode] = useState(bulkEditByDefault)

  // SCRUM-1032: the guided setup offers the usual entries for this dataset as
  // one-click chips, so a new controller does not start from a blank grid.
  const isGuidedSetup = useIsGuidedSetup()
  const { data: existingList, refetch: refetchExisting } = useSearchEmitter(
    WHOLE_LIST_PARAMS,
    Number(interfaceId || 0),
    { enabled: isGuidedSetup && Number(interfaceId) > 0 } as any,
  )
  const existingNames = ((existingList?.list ?? []) as any[]).map((row) =>
    String(row?.emitter ?? ''),
  )

  // Names staged in the grid but not yet written, so a chip reads as added the
  // moment it is clicked rather than only after the autosave lands.
  const [stagedNames, setStagedNames] = useState<string[]>([])
  const addedNames = useQuickAddNames(
    existingNames,
    stagedNames,
    refetchExisting,
    isGuidedSetup,
  )

  /**
   * A chip stages a draft row; it does not create the emitter. The row is
   * written by the same autosave as anything typed into the grid, which leaves
   * it renameable and droppable in between — a chip clicked by mistake is a
   * delete on a staged row, not a record on the controller.
   *
   * The efficiency comes with it: emitter type is most of what determines it,
   * and a blank cell in a column the sizing maths divides by is a worse
   * starting point than an industry mid-range figure the user can audit.
   */
  const handleQuickAdd = (name: string) => {
    gridActions.current?.addDraft({
      emitter: name,
      efficiency: EMITTER_EFFICIENCY_PRESETS[name],
    })
  }
  const [rowCount, setRowCount] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')

  // The grid owns its own data loading (infinite row model + datasource); the
  // page only drives bulk edit mode and the delete modal.
  const gridActions = useRef<EmitterGridActions | null>(null)

  const handleRowCountChange = useCallback((count: number) => {
    setRowCount(count)
  }, [])

  // Search runs on the server (Keyword param), so it matches every emitter, not
  // just the blocks the grid has already fetched.
  const handleSearch = useMemo(
    () =>
      debounce((event: SelectChangeEvent) => {
        setKeyword(event.target.value.toString().trim())
      }, 500),
    [],
  )

  const handleToggleBulkEdit = async () => {
    if (bulkEditMode) {
      const result = await gridActions.current?.save()
      // Stay in bulk edit mode when some rows failed, so the user can retry
      // them instead of losing the pending changes.
      if (result && result.failed > 0) return
    }
    setBulkEditMode(!bulkEditMode)
  }

  const handleCancelBulkEdit = () => {
    gridActions.current?.cancel()
    setBulkEditMode(false)
  }

  const handleDeleteItem = (
    id: string,
    itemInterfaceId: string,
    name: string,
  ) => {
    if (isViewOnly) return

    openModal()
    setModalView({
      type: 'DELETE_EMITTER_VIEW',
      id: id,
      interfaceId: itemInterfaceId,
      name,
      onSuccess: async () => {
        // The row is gone: a staged edit on it could never land.
        gridActions.current?.forgetRow(String(id))
        gridActions.current?.refresh()
      },
    })
  }

  return (
    <Stack className={classes.wrapper}>
      {/* Wraps rather than overlaps: in the four-column Station Setup layout
          these panels squeeze to ~200px, where the title and the bulk-edit
          buttons collided and "Save" was clipped to "S". */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap="8px"
        mb="12px"
        sx={{ minWidth: 0 }}
      >
        {titleSlot ?? (
          <SearchInput
            name={t('formsPlaceholdersSearchEmitter')}
            onChange={handleSearch}
          />
        )}

        <Stack direction="row" spacing={1.5}>
          {/* SCRUM-1032: the guided wizard owns saving — its Next button
              flushes every grid — so the per-grid Cancel / Save (and the Edit
              toggle, since bulk edit is always on there) are not shown. */}
          {!bulkEditByDefault &&
            (bulkEditMode ? (
              <>
                <ButtonField
                  startIcon={<CancelIcon />}
                  style={{
                    height: '32px',
                    minWidth: '70px',
                    borderRadius: '5px',
                    backgroundColor: '#fff2f2',
                    color: '#dc3545',
                    border: '1px solid #f3c2c5',
                  }}
                  name={t('commonActionsCancel')}
                  onClick={handleCancelBulkEdit}
                />
                <ButtonField
                  startIcon={<SaveIcon />}
                  style={{
                    height: '32px',
                    minWidth: '70px',
                    borderRadius: '5px',
                    backgroundColor: '#004744',
                    color: '#fff',
                  }}
                  name={t('commonActionsSave')}
                  onClick={handleToggleBulkEdit}
                />
              </>
            ) : (
              <ButtonField
                startIcon={<EditIcon />}
                style={{
                  height: '32px',
                  minWidth: '89px',
                  borderRadius: '5px',
                  opacity: isViewOnly ? 0.6 : 1,
                }}
                name={t('commonActionsEdit')}
                onClick={handleToggleBulkEdit}
                disabled={isViewOnly}
              />
            ))}
        </Stack>
      </Stack>

      <MotionDiv
        initial="initial"
        animate="animate"
        variants={flyInVariantsDashboard}
        className={classes.content}
      >
        {/* Keep the grid mounted while empty in bulk edit mode: the create row
            is pinned to it, so unmounting would remove the only way to add the
            first emitter. */}
        {rowCount === 0 && !bulkEditMode ? (
          <NoData text={t('commonStatusNoData')} />
        ) : (
          <EmitterTable
            interfaceId={Number(interfaceId)}
            keyword={keyword}
            bulkEditMode={bulkEditMode}
            onDeleteItem={handleDeleteItem}
            onRowCountChange={handleRowCountChange}
            onStagedNamesChange={setStagedNames}
            actionsRef={gridActions}
          />
        )}
      </MotionDiv>

      {isGuidedSetup && (
        <QuickAddChips
          title="Common emitters"
          items={EMITTER_PRESETS}
          existingNames={addedNames}
          onAdd={handleQuickAdd}
          disabled={isViewOnly}
        />
      )}
    </Stack>
  )
}

export default Emitter
